import {
	FeaturesAndCapabilitiesResponsePayloadService,
	ExtendedCapability,
	FeaturesAndCapabilitiesResponsePayloadServiceCategoryExt,
	FeaturesAndCapabilitiesResponsePayloadServiceCategorySub,
	FeaturesAndCapabilitiesResponsePayloadServiceCategorySubObject,
	TERM_UNIT,
	BrandCode,
	FuelType
} from '../@types/dataTypes';
import _ from 'lodash';
import {
	AppLinksV2Labels,
	KeyValue,
	TrialEligibility,
	VinCheckerCSFeaturesMainLabels
} from '../@types/labelTypes';
import { CONSTANTS } from '../constants/constants';

const { ORDER_OF_SERVICES } = CONSTANTS;

type replaceWithLabelProps = {
	labelList: KeyValue;
	key: string;
};
type MapCapabilitiesToLabelProps = {
	capabilities: ExtendedCapability;
	labels: { [p: string]: string };
};

const replaceWithLabel = ({
	labelList,
	key
}: replaceWithLabelProps): string => {
	if (key && labelList[key]) {
		return labelList[key] as string;
	} else {
		return key;
	}
};

const mapCapabilitiesToLabel = ({
	capabilities,
	labels
}: MapCapabilitiesToLabelProps): ExtendedCapability => {
	const updatedCapabilities: ExtendedCapability = {};
	Object.entries(capabilities).forEach(([capabilityKey, capabilityValue]) => {
		const newKey = replaceWithLabel({
			labelList: labels,
			key: capabilityKey
		});
		updatedCapabilities[newKey] = capabilityValue;
	});
	return updatedCapabilities;
};

// eslint-disable-next-line max-lines-per-function
const mapLabelsWithVehicleData = (
	connectedFeaturesData: FeaturesAndCapabilitiesResponsePayloadService[],
	labels: VinCheckerCSFeaturesMainLabels
): FeaturesAndCapabilitiesResponsePayloadService[] => {
	const servicesList = labels.vinCheckerCSFeaturesList.servicesList,
		categoryList = labels.vinCheckerCSFeaturesList.categoryList,
		subCategoryList = labels.vinCheckerCSFeaturesList.subCategoryList,
		extendedCapabilityList =
			labels.vinCheckerCSFeaturesList.extendedCapabilityList;
	const labelsMappedToData = _.cloneDeep(connectedFeaturesData);

	if (Array.isArray(labelsMappedToData)) {
		labelsMappedToData.forEach(
			// eslint-disable-next-line max-lines-per-function
			(service: FeaturesAndCapabilitiesResponsePayloadService) => {
				// eslint-disable-next-line max-lines-per-function
				service.category?.forEach((category) => {
					if (
						`extendedCapability` in category &&
						category.extendedCapability
					) {
						category.extendedCapability = mapCapabilitiesToLabel({
							capabilities: category.extendedCapability,
							labels: extendedCapabilityList
						});
					} else if (
						`subCategory` in category &&
						category.subCategory
					) {
						category.subCategory.forEach((subCategory) => {
							if (subCategory.extendedCapability) {
								subCategory.extendedCapability =
									mapCapabilitiesToLabel({
										capabilities:
											subCategory.extendedCapability,
										labels: extendedCapabilityList
									});
							}

							subCategory.subCategoryName = replaceWithLabel({
								labelList: subCategoryList,
								key: subCategory.subCategoryName as string
							});
						});
					}

					category.categoryName = replaceWithLabel({
						labelList: categoryList,
						key: category.categoryName as string
					});
				});

				service.serviceName = replaceWithLabel({
					labelList: servicesList,
					key: service.serviceName as string
				});
			}
		);
	}
	return labelsMappedToData;
};

const updateOrderOfServices = (
	services: FeaturesAndCapabilitiesResponsePayloadService[]
): FeaturesAndCapabilitiesResponsePayloadService[] => {
	const servicesIndexMap = ORDER_OF_SERVICES.reduce(
		(acc: Record<string, number>, service: string, index: number) => {
			acc[service] = index;
			return acc;
		},
		{} as Record<string, number>
	);
	return [...services].sort((a, b) => {
		const indexA = servicesIndexMap[a.serviceName] ?? Infinity;
		const indexB = servicesIndexMap[b.serviceName] ?? Infinity;
		return indexA - indexB;
	});
};

const shouldDisplaySubCategory = (
	subCategory: FeaturesAndCapabilitiesResponsePayloadServiceCategorySubObject
): boolean => {
	if (subCategory.extendedCapability) {
		return _.some(
			Object.values(subCategory.extendedCapability).map(Boolean)
		);
	}
	return false;
};

const shouldDisplayCategory = (
	category:
		| FeaturesAndCapabilitiesResponsePayloadServiceCategoryExt
		| FeaturesAndCapabilitiesResponsePayloadServiceCategorySub
): boolean => {
	if (`extendedCapability` in category) {
		if (category.extendedCapability) {
			return _.some(
				Object.values(category.extendedCapability).map(Boolean)
			);
		}
	} else if (`subCategory` in category) {
		if (category.subCategory) {
			return _.some(category.subCategory, shouldDisplaySubCategory);
		}
	}
	return false;
};

const shouldDisplayService = (
	service: FeaturesAndCapabilitiesResponsePayloadService
): boolean => {
	return _.some(_.map(service.category, shouldDisplayCategory));
};

const servicesToDisplay = (
	services: FeaturesAndCapabilitiesResponsePayloadService[]
): FeaturesAndCapabilitiesResponsePayloadService[] => {
	return _.filter(services, (service) => shouldDisplayService(service));
};

const replaceBrandFromLabel = (label: string): string => {
	const regex = /\{\{brand\}\}/gi;
	const brandName =
		window.dxp.settings.brand === `toyota` ? `Toyota` : `Lexus`;
	return label.replace(regex, brandName);
};

const replaceApplicationFromLabel = (label: string): string => {
	const regex = /\{\{app\}\}/gi;
	const appName =
		window.dxp.settings.brand === `toyota` ? `MyToyota` : `Lexus Link+`;
	return label.replace(regex, appName);
};

const replacePlaceHoldersInLabel = (label: string): string =>
	replaceApplicationFromLabel(replaceBrandFromLabel(label));

const replacePlaceHoldersInLabelObject = (
	input: string | Record<string, any>
): string | Record<string, any> => {
	if (typeof input === `string`) {
		return replacePlaceHoldersInLabel(input);
	}

	if (typeof input === `object` && input !== null) {
		const output: any = Array.isArray(input) ? [] : {};
		for (const key in input) {
			output[key] = replacePlaceHoldersInLabelObject(input[key]);
		}
		return output;
	}

	return input;
};

const replaceTrialTermValueFromLabel = (
	label: string,
	value: string
): string => {
	const regex = /\{\{value\}\}/g;
	return label.replace(regex, value);
};

const determineLabelWithTermUnit = (
	labels: TrialEligibility,
	termUnit:
		| typeof TERM_UNIT.DAYS
		| typeof TERM_UNIT.MONTHS
		| typeof TERM_UNIT.YEARS,
	value: string
): string => {
	switch (termUnit) {
		case `YRS`:
			return replaceTrialTermValueFromLabel(labels.trialYears, value);
		case `MTH`:
			return replaceTrialTermValueFromLabel(labels.trialMonths, value);
		default:
			return replaceTrialTermValueFromLabel(labels.trialDays, value);
	}
};

const removeBracketFromUrlIfFallbackLabels = (
	labels: AppLinksV2Labels
): AppLinksV2Labels => {
	return {
		...labels,
		playStoreUrl: labels.playStoreUrl.startsWith(`[`)
			? labels.playStoreUrl.slice(1, -1)
			: labels.playStoreUrl,
		appleStoreUrl: labels.appleStoreUrl.startsWith(`[`)
			? labels.appleStoreUrl.slice(1, -1)
			: labels.appleStoreUrl
	};
};

const isValidVin = (vin: string): boolean => /^[0-9A-Za-z]{17}$/.test(vin);

const removeBrandAndFuelTypeFromModel = (model: string): string => {
	const keyWordsToRemove = [
		...new Set(
			[
				...Object.values(FuelType),
				...Object.values(BrandCode),
				`PHEV`,
				`HEV`
			].map((value) => value.toLowerCase())
		)
	];

	let cleanedModel = model;

	for (const keyword of keyWordsToRemove) {
		const regex = new RegExp(`\\b${keyword}\\b`, `gi`);
		cleanedModel = cleanedModel.replace(regex, ``);
	}

	return cleanedModel.replace(/\s+/g, ` `).trim();
};

export {
	mapLabelsWithVehicleData,
	shouldDisplayCategory,
	shouldDisplaySubCategory,
	shouldDisplayService,
	servicesToDisplay,
	replaceWithLabel,
	replaceTrialTermValueFromLabel,
	determineLabelWithTermUnit,
	removeBracketFromUrlIfFallbackLabels,
	updateOrderOfServices,
	isValidVin,
	replacePlaceHoldersInLabel,
	replacePlaceHoldersInLabelObject,
	removeBrandAndFuelTypeFromModel
};
